/**
 * Pure relevantie-scoring (geen Firebase) — gedeeld door de shop-sortering (ArticleCatalog),
 * de admin en de catalogus (nav-foto = meest relevante product). De verkoop-/GA4-data komt uit
 * `settings/popularity` (zie popularity.ts); voor plekken zonder live data gebruik je EMPTY_POPULARITY.
 */
export interface Popularity {
  /** Verkochte aantallen per slug (uit orders, laatste 30 dagen). */
  units: Record<string, number>;
  /** GA4 item-weergaven per slug (alleen als GA4 is ingesteld). */
  views: Record<string, number>;
  /** "Vaak samen besteld": per slug de vaakst mee-bestelde slugs (uit orders). */
  coPurchase: Record<string, Record<string, number>>;
  /**
   * Idem, maar uit WINKELWAGENS (samen in één mandje gelegd — ook zonder afrekenen).
   * Veel meer volume dan orders, dus dit signaal is er veel eerder; orders blijven leidend.
   */
  coCart: Record<string, Record<string, number>>;
}

export const EMPTY_POPULARITY: Popularity = { units: {}, views: {}, coPurchase: {}, coCart: {} };

function topSlugs(m: Record<string, number> | undefined, limit: number): string[] {
  if (!m) return [];
  return Object.entries(m)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([s]) => s);
}

/** Top mee-bestelde slugs voor één product (hoogste co-aankoop eerst). */
export function coPurchasedSlugs(pop: Popularity, slug: string, limit = 8): string[] {
  return topSlugs(pop.coPurchase[slug], limit);
}

/** Top samen-in-de-winkelwagen-gelegde slugs (zwakker signaal dan een echte order). */
export function coCartSlugs(pop: Popularity, slug: string, limit = 8): string[] {
  return topSlugs(pop.coCart[slug], limit);
}

/** Signalen voor de relevantie-score van één artikel (subset van Article). */
export interface RelevanceInput {
  slug: string;
  onRequest?: boolean;
  inStock?: boolean;
  image?: string;
  variantCount?: number;
  variantThumbs?: unknown[];
  category?: string;
  groep?: string;
  subgroep?: string;
}

/**
 * Online te configureren en af te rekenen, terwijl de catalogus "op aanvraag" + €0 zegt:
 * ventilatieroosters (prijs per lengte/kleur-staffel) en voorzetramen (prijs per maat).
 *
 * Zonder deze uitzondering kregen ze de "geen prijs"-straf (−100.000) en stonden alle 24 roosters
 * op ± −99.000 in de admin — onderaan elke lijst, terwijl ze gewoon besteld kunnen worden
 * (13-09-2026). De toebehoren (beglazingsrubbers) hebben wél een eigen prijs en vallen erbuiten.
 */
export function onlineConfigureerbaar(a: RelevanceInput): boolean {
  const groep = a.groep ?? "";
  if (a.category === "ventilatieroosters" && /roosters/i.test(groep) && !/toebehoren/i.test(groep)) return true;
  return /voorzetram/i.test(groep) || /voorzetram/i.test(a.subgroep ?? "");
}

export interface RelevancePunt {
  label: string;
  punten: number;
}

/**
 * De score uitgesplitst per signaal — dezelfde optelsom als relevanceScore, zodat de admin kan
 * laten zien wáárom twee artikelen verschillen (het verschil tussen ± 4.000 en ± 500 is vrijwel
 * altijd de voorraadstand bij Timmers: +3.000 tegen −500).
 */
export function relevanceParts(a: RelevanceInput, priceCents: number, pop: Popularity, marginPct = 0): RelevancePunt[] {
  const configureerbaar = onlineConfigureerbaar(a);
  const delen: RelevancePunt[] = [];
  if (!configureerbaar && (a.onRequest || priceCents <= 0)) delen.push({ label: "Op aanvraag / geen prijs", punten: -100000 });
  // Voorraad: een op-maat-product (rooster, voorzetraam) wordt altijd gemaakt; "niet op voorraad"
  // zegt daar niets over en telt dus neutraal.
  delen.push(
    configureerbaar
      ? { label: "Op maat gemaakt", punten: 0 }
      : a.inStock !== false
        ? { label: "Op voorraad", punten: 3000 }
        : { label: "Op bestelling", punten: -500 },
  );
  delen.push({ label: "Foto", punten: a.image ? 800 : 0 });
  delen.push({ label: "Uitvoeringen", punten: (a.variantCount ?? 1) > 1 || (a.variantThumbs?.length ?? 0) > 0 ? 300 : 0 });
  delen.push({ label: "Marge", punten: Math.min(Math.max(marginPct, 0), 60) * 8 }); // max +480
  delen.push({ label: "Verkocht (30d)", punten: Math.min(pop.units[a.slug] ?? 0, 25) * 200 }); // dominant, max +5000
  delen.push({ label: "GA4-weergaven", punten: Math.min(pop.views[a.slug] ?? 0, 200) * 3 }); // max +600
  return delen;
}

/**
 * "Relevantie"-score van een artikel — dezelfde formule overal. Beschikbaarheid leidt,
 * verkoopsnelheid is het zwaarste signaal, marge + GA4-weergaven geven een duwtje;
 * 'op aanvraag'/geen prijs zakt naar de bodem.
 *
 * `marginPct` = de door de admin ingestelde marge/opslag (%) op dit product (per variant/artikel
 * of per categorie). Zo verandert de score als je de marge aanpast.
 *
 * De dealerkorting zat hier eerder als extra onderscheider in, maar dat percentage mag niet in de
 * publieke bundel staan (het verraadt onze inkoopprijs). Het signaal is niet weg: de catalogus
 * wordt bij de build al op die statische score gesorteerd (transform-articles.mjs) en omdat
 * Array.sort stabiel is, valt gelijkspel hieronder precies op die volgorde terug.
 */
export function relevanceScore(a: RelevanceInput, priceCents: number, pop: Popularity, marginPct = 0): number {
  return relevanceParts(a, priceCents, pop, marginPct).reduce((som, d) => som + d.punten, 0);
}
